import React, { useEffect, useRef, useState } from "react";
import { FlightDetails, ConnectionFlight, FlightSegment } from "../types";

export interface SelectedSegment {
  segment: FlightSegment;
  connectionId: string; // Parent connection's compositeId
}

const flightKey = (flight: FlightDetails) =>
  flight.compositeId || String(flight.id);

const segmentKey = (segment: FlightSegment) =>
  segment.compositeId || String(segment.id);

const connectionKey = (connection: ConnectionFlight) =>
  connection.compositeId ||
  `${connection.segments[0]?.id}_${connection.overallDepartureTime}`;

// Owns the search-results selection model (direct flights + individual
// connection segments) and the toggle helpers. Clears itself whenever the
// search params (not just the page) change.
export function useFlightSelection(
  stopsFilter: string | null,
  searchDate: string,
  selectedDates: string[],
) {
  const [selectedFlights, setSelectedFlights] = useState<FlightDetails[]>([]);
  const [selectedSegments, setSelectedSegments] = useState<SelectedSegment[]>(
    [],
  );

  const prevSearchParams = useRef({
    stopsFilter,
    searchDate,
    selectedDates: JSON.stringify(selectedDates),
  });

  useEffect(() => {
    const paramsChanged =
      prevSearchParams.current.stopsFilter !== stopsFilter ||
      prevSearchParams.current.searchDate !== searchDate ||
      prevSearchParams.current.selectedDates !== JSON.stringify(selectedDates);

    if (paramsChanged) {
      setSelectedFlights([]);
      setSelectedSegments([]);
    }

    prevSearchParams.current = {
      stopsFilter,
      searchDate,
      selectedDates: JSON.stringify(selectedDates),
    };
  }, [stopsFilter, searchDate, selectedDates]);

  const toggleFlightSelection = (flight: FlightDetails) => {
    const key = flightKey(flight);
    setSelectedFlights((prev) =>
      prev.some((f) => flightKey(f) === key)
        ? prev.filter((f) => flightKey(f) !== key)
        : [...prev, flight],
    );
  };

  const isSegmentSelected = (
    segment: FlightSegment,
    connectionId: string,
  ): boolean => {
    const key = segmentKey(segment);
    return selectedSegments.some(
      (sel) =>
        segmentKey(sel.segment) === key && sel.connectionId === connectionId,
    );
  };

  const areAllSegmentsSelected = (connection: ConnectionFlight): boolean => {
    const connectionId = connectionKey(connection);
    return connection.segments.every((segment) =>
      isSegmentSelected(segment, connectionId),
    );
  };

  const toggleSegmentSelection = (
    segment: FlightSegment,
    connectionId: string,
    event?: React.MouseEvent,
  ) => {
    if (event) event.stopPropagation(); // Prevent parent click handlers
    const key = segmentKey(segment);
    // Derive from `prev` (pure updater) so a segment can never be added twice.
    setSelectedSegments((prev) => {
      const matches = (sel: SelectedSegment) =>
        segmentKey(sel.segment) === key && sel.connectionId === connectionId;
      return prev.some(matches)
        ? prev.filter((sel) => !matches(sel))
        : [...prev, { segment, connectionId }];
    });
  };

  const toggleAllSegmentsInConnection = (connection: ConnectionFlight) => {
    const connectionId = connectionKey(connection);
    setSelectedSegments((prev) => {
      const selectedKeys = new Set(
        prev
          .filter((sel) => sel.connectionId === connectionId)
          .map((sel) => segmentKey(sel.segment)),
      );
      const allSelected = connection.segments.every((segment) =>
        selectedKeys.has(segmentKey(segment)),
      );
      if (allSelected) {
        return prev.filter((sel) => sel.connectionId !== connectionId);
      }
      const toAdd = connection.segments
        .filter((segment) => !selectedKeys.has(segmentKey(segment)))
        .map((segment) => ({ segment, connectionId }));
      return [...prev, ...toAdd];
    });
  };

  const clearSelection = () => {
    setSelectedFlights([]);
    setSelectedSegments([]);
  };

  return {
    selectedFlights,
    selectedSegments,
    selectedCount: selectedFlights.length + selectedSegments.length,
    toggleFlightSelection,
    isSegmentSelected,
    areAllSegmentsSelected,
    toggleSegmentSelection,
    toggleAllSegmentsInConnection,
    clearSelection,
  };
}
